import { SpecificationEnum } from '@core/procedure-helpers/models/specification.enum';
import { Tooth } from '@modules/teeth-diagram/models/tooth';
import { UnitTypes } from '@modules/teeth-diagram/models/unit-type.enum';

export type IntuitiveCopyFromSourceEligibilityDeps = {
	readonly isDefaultUnitType: (unitTypeId: number | null | undefined) => boolean;
};

const hasRestorativeDetailFields = (tooth: Tooth): boolean => {
	const specId = tooth.SpecificationId;
	const hasSpecification = specId != null && specId !== SpecificationEnum.None;
	const hasMaterial = tooth.MaterialID != null && String(tooth.MaterialID) !== '';
	const hasShadeBody = !!(tooth.ShadeBody && String(tooth.ShadeBody).trim());
	const hasShadeOther = [
		tooth.ShadeIncisal && String(tooth.ShadeIncisal).trim(),
		tooth.ShadeGingival && String(tooth.ShadeGingival).trim()
	].some(Boolean);
	const hasPrep = [tooth.PreparationDesignBuccalId, tooth.PreparationDesignLingualId].some(Boolean);
	const hasMargin = [tooth.MarginDesignBuccalId, tooth.MarginDesignLingualId].some(Boolean);
	const hasStump = tooth.StumpfShade != null && String(tooth.StumpfShade).trim() !== '';
	const hasPontic = tooth.PonticDesignID != null;

	return [hasSpecification, hasMaterial, hasShadeBody, hasShadeOther, hasPrep, hasMargin, hasStump, hasPontic].some(Boolean);
};

export const hasTreatmentInfo = (tooth: Tooth, deps: IntuitiveCopyFromSourceEligibilityDeps): boolean => {
	const toothId = tooth.ToothID ?? 0;

	if (!toothId) {
		return false;
	}

	const unitType = tooth.UnitTypeID;

	if (unitType === UnitTypes.Missing || unitType === UnitTypes.Missing_edentulousSpace || unitType === UnitTypes.Missing_noSpace) {
		return true;
	}

	if (deps.isDefaultUnitType(unitType)) {
		return hasRestorativeDetailFields(tooth);
	}

	return true;
};

export const filterSelectedTeethFromSources = (sourceTeeth: Tooth[], selectedToothIds: number[]): Tooth[] => {
	const sourceTeethIds = new Set(sourceTeeth.map(source => source.ToothID as number));
	const hasNonSourceSelection = selectedToothIds.some(selectedId => !sourceTeethIds.has(selectedId));

	if (hasNonSourceSelection) {
		return sourceTeeth;
	}

	return sourceTeeth.filter(source => !selectedToothIds.includes(source.ToothID as number));
};

export const isIntuitiveCopyFromMenuEnabled = (selectedToothIds: number[], sourceCount: number): boolean =>
	selectedToothIds.length > 0 && sourceCount > 0;
